import React, { useMemo } from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { Button, Space, Dropdown, Input, Modal, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { EBillingStatus, EBillingStatusLabel } from 'types/provider';
import StatusLabel from 'components/ManageSubscription/components/StatusLabel';
import { ColumnType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import {
    EProListingBillingStatus,
    EProListingOrderKey,
    TBillingProListingFeeItem,
    TGetPaymentBillingProListingFeeList,
} from 'types/financials';
import { isLocalhost } from 'utils/common';
import { getUserToken } from 'utils/localstore';
import axios from 'axios';
import usePaymentBillingProListingFeeList from 'hooks/usePaymentBillingProListingFeeList';
import BillingPortalButton from 'components/BillingPortalButton';
import FinancialsLandingPage from '../FinancialsLandingPage';
import useKanmonConnect from 'hooks/useKanmonConnect';
import commonS from 'styles/common.module.less';
import { getInvoiceViewUrl } from 'api/financials';
import ProviderCell from 'components/ProviderCell';

interface Props {
    clientId?: number;
    isPractice?: boolean;
}
const BillingInvoiceTable = ({ clientId, isPractice = false }: Props) => {
    const [specialtyFilter, setSpecialtyFilter] = React.useState<string[]>([]);
    const [searchInvoiceIdText, setSearchInvoiceIdText] = React.useState('');
    const [searchPendingInvoiceIdText, setSearchPendingInvoiceIdText] = React.useState('');
    const [autoShowKanmonModal, setAutoShowKanmonModal] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [orderType, setOrderType] = React.useState<'ASC' | 'DESC'>('DESC');
    const [orderKey, setOrderKey] = React.useState<EProListingOrderKey | undefined>(undefined);
    const [billingStatus, setBillingStatus] = React.useState<EProListingBillingStatus[]>([]);
    const [kanmonConnect, kanmonInfo, loadingKanmonConnect, refetchKanmonInfo] = useKanmonConnect();
    const [showPaylaterModal, setShowPaylaterModal] = React.useState(false);

    const [viewingId, setViewingId] = React.useState<number | undefined>(undefined);

    const [dataList, listTotal, loading, refetch] = usePaymentBillingProListingFeeList(undefined, isPractice);

    React.useEffect(() => {
        if (autoShowKanmonModal && kanmonConnect) {
            setShowPaylaterModal(false);
            kanmonConnect.show();
        }
    }, [autoShowKanmonModal, kanmonConnect]);
    // 处理表格变化
    const handleTableChange = (paginationData: any, filtersData?: any, sorter?: any, action?: any) => {
        if (action?.action === 'paginate') {
            setPage(paginationData.current!);
            return;
        }

        if (sorter) {
            setOrderType(sorter.order === 'ascend' ? 'ASC' : 'DESC');
            if (sorter.column?.dataIndex === 'proListingFee') {
                setOrderKey(EProListingOrderKey.PRO_LISTING_FEE);
            }
            if (sorter.column?.dataIndex === 'issuedDate') {
                setOrderKey(EProListingOrderKey.ISSUED_DATE);
            }
        }
        if (filtersData) {
            setBillingStatus(filtersData.billingStatus || []);
        }
    };

    const refetchData = () => {
        const filter: Partial<TGetPaymentBillingProListingFeeList> = {};
        filter.current = page;
        filter.size = 10;
        if (orderKey) {
            filter.proListingFeeOrderKey = orderKey;
        }
        if (orderType) {
            filter.orderType = orderType;
        }
        if (billingStatus && billingStatus.length > 0) {
            filter.billingStatus = billingStatus;
        }
        if (searchInvoiceIdText) {
            filter.billingId = searchInvoiceIdText;
        }
        refetch(filter);
    };

    const handleDownload = async (item: TBillingProListingFeeItem) => {
        const baseURL = isLocalhost() ? 'https://dev.boxpractice.com/' : `${window.location.origin}/`;
        const url = `${baseURL}schedule-server/provider/stripe/invoice/${item.billingId}/pdf-url?billingId=${item.billingId}`;
        const response = await axios.get(url, {
            responseType: 'blob',
            headers: {
                accept: 'application/json, text/plain, */*',
                'accept-language': 'en-GB,en-US;q=0.9,en;q=0.8',
                'cache-control': 'no-cache',
                origin: `${baseURL}`,
                pragma: 'no-cache',
                priority: 'u=1, i',
                referer: `${baseURL}`,
                // 'sec-ch-ua': '"Google Chrome";v="135", "Not-A.Brand";v="8", "Chromium";v="135"',
                // 'sec-ch-ua-mobile': '?0',
                // 'sec-ch-ua-platform': '"macOS"',
                // 'sec-fetch-dest': 'empty',
                // 'sec-fetch-mode': 'cors',
                // 'sec-fetch-site': 'cross-site',
                token: getUserToken(),
                // 'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36',
                // 'Cookie': 'AWSALB=HH/eRDC1k7Dn6U2etvWV4a4ZkQCrxxBlk1kwXGDIoCiDu+gynKNqanH4yzp59vf8Gi3IXv45aZlQRcOmy9vP7YP5MvPl82D33rnWWonlXyKt7wASSlOUgKXp3LTD; AWSALBCORS=HH/eRDC1k7Dn6U2etvWV4a4ZkQCrxxBlk1kwXGDIoCiDu+gynKNqanH4yzp59vf8Gi3IXv45aZlQRcOmy9vP7YP5MvPl82D33rnWWonlXyKt7wASSlOUgKXp3LTD'
            },
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = downloadUrl;
        a.download = `${item?.billingId}.pdf`; // 可以根据需要调整

        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    };

    const handleView = async (item: TBillingProListingFeeItem) => {
        setViewingId(item.billingId);
        const res = await getInvoiceViewUrl(item.billingId);
        if (!res.error) {
            const url = res.data.data;
            if (url) {
                window.open(url, '_blank');
            } else {
                message.error('Failed to get the invoice view url');
            }
        }
        setViewingId(undefined);
    };

    React.useEffect(() => {
        refetchData();
    }, [page, orderType, orderKey, billingStatus, specialtyFilter, searchInvoiceIdText]);

    const allColumns: ColumnType<TBillingProListingFeeItem>[] = [
        {
            title: 'INVOICE ID',
            dataIndex: 'billingId',
            key: 'billingId',
            width: 120,
            filterDropdown: (props: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search by invoice Id"
                        value={searchPendingInvoiceIdText}
                        onChange={(e) => setSearchPendingInvoiceIdText(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSearchInvoiceIdText(searchPendingInvoiceIdText);
                                props.confirm();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setSearchPendingInvoiceIdText('');
                                setSearchInvoiceIdText('');
                                props.clearFilters();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined />,
            onFilter: () => true, // 我们使用自己的过滤逻辑
        },
        {
            title: 'PRO-LISTINGS FEE',
            dataIndex: 'proListingFee',
            key: 'proListingFee',
            width: 200,
            sorter: (a: TBillingProListingFeeItem, b: TBillingProListingFeeItem) => a.proListingFee - b.proListingFee,
            render: (fee: number) => {
                return <div className={s.tableItem}>${fee}</div>;
            },
        },
        ...(isPractice
            ? [
                  {
                      title: 'PROVIDER',
                      key: 'providerName',
                      width: 150,
                      render: (_: string, record: TBillingProListingFeeItem) => (
                          <ProviderCell
                              name={record.providerName}
                              avatar={record.providerAvatar}
                              credential={record.providerCredential}
                          />
                      ),
                  } as ColumnType<TBillingProListingFeeItem>,
              ]
            : []),
        {
            title: 'STATUS',
            dataIndex: 'billingStatus',
            key: 'billingStatus',
            width: 150,
            render: (status: EBillingStatus, item: TBillingProListingFeeItem) => {
                return <StatusLabel value={status} />;
            },
            // export enum EBillingStatus {
            //     DISPUTE_OPEN = "Dispute open",
            //     DISPUTE_CLOSED = "Dispute closed",
            //     PENDING = "Pending",
            //     PAID = "Paid",
            //     FAILED = "Failed",
            //     CANCELLED = "Cancelled",
            //     NOT_READY = "Not ready",
            //     PROCESSING = "Processing",
            //     SUCCEEDED = "Succeeded",
            //     REFUND_PROCESSING = "Refund processing",
            //     REFUNDED = "Refunded",
            //     REFUND_FAILED = "Refund failed",
            //     DISPUTE_PROCESSING = "Dispute processing",
            //     DISPUTE_WON = "Dispute won",
            //     DISPUTE_LOST = "Dispute lost",
            // }
            filters: [
                {
                    value: EProListingBillingStatus.PENDING,
                    text: EBillingStatusLabel.PENDING,
                },
                {
                    value: EProListingBillingStatus.PROVIDER_NOT_READY,
                    text: EBillingStatusLabel.NOT_READY,
                },
                {
                    value: EProListingBillingStatus.PROCESSING,
                    text: EBillingStatusLabel.PROCESSING,
                },
                {
                    value: EProListingBillingStatus.SUCCEEDED,
                    text: EBillingStatusLabel.SUCCEEDED,
                },
                {
                    value: EProListingBillingStatus.FAILED,
                    text: EBillingStatusLabel.FAILED,
                },
                {
                    value: EProListingBillingStatus.CANCELED,
                    text: EBillingStatusLabel.CANCELED,
                },
            ],
            filteredValue: billingStatus,
            onFilter: (value: any, record: TBillingProListingFeeItem) => {
                return true;
            },
        },
        {
            title: 'ISSUED DATE',
            dataIndex: 'issuedDate',
            key: 'issuedDate',
            width: 300,
            render: (issuedDate: string, item: TBillingProListingFeeItem) => {
                if (!item.issuedDate || !item.issuedDate) {
                    return '-';
                }
                // 03/26/2023, 10:30 - 11:00 AM
                return <div className={s.date}>{dayjs(issuedDate).format('MM/DD/YYYY')}</div>;
            },
            sorter: (a: TBillingProListingFeeItem, b: TBillingProListingFeeItem) => {
                return new Date(a.issuedDate).getTime() - new Date(b.issuedDate).getTime();
            },
        },
        {
            title: '',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (record: TBillingProListingFeeItem, item: TBillingProListingFeeItem) => {
                if (item.billingStatus === EProListingBillingStatus.FAILED) {
                    return (
                        <div className={s.tableActions}>
                            <BillingPortalButton type="link" text="Pay now" className={s.viewButton} hideIcon />
                            <Button
                                onClick={() => {
                                    setShowPaylaterModal(true);
                                }}
                                type="link"
                                className={s.viewButton}
                            >
                                Pay later
                            </Button>
                        </div>
                    );
                }

                const showDownloadInvoice =
                    record.externalInvoicePlatform === 'STRIPE' && record.billingId && record.externalInvoiceId;
                if (!showDownloadInvoice) {
                    return null;
                }
                const items: any = [
                    {
                        key: 'Download invoice',
                        label: (
                            <div
                                onClick={() => {
                                    handleDownload(record);
                                }}
                                className={s.popItem}
                            >
                                Download invoice
                            </div>
                        ),
                    },
                ].filter(Boolean);
                return (
                    <div className={s.tableActions}>
                        <Button
                            type="link"
                            onClick={() => {
                                handleView(record);
                            }}
                            loading={viewingId === record.billingId}
                            className={s.viewButton}
                        >
                            View
                        </Button>
                        {showDownloadInvoice && (
                            <Dropdown
                                menu={{
                                    items,
                                }}
                                trigger={['hover']}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M12.0004 7.20039C10.6749 7.20039 9.60039 6.12587 9.60039 4.80039C9.60039 3.47491 10.6749 2.40039 12.0004 2.40039C13.3259 2.40039 14.4004 3.47491 14.4004 4.80039C14.4004 6.12587 13.3259 7.20039 12.0004 7.20039Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M12.0004 14.4004C10.6749 14.4004 9.60039 13.3259 9.60039 12.0004C9.60039 10.6749 10.6749 9.60039 12.0004 9.60039C13.3259 9.60039 14.4004 10.6749 14.4004 12.0004C14.4004 13.3259 13.3259 14.4004 12.0004 14.4004Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M9.60039 19.2004C9.60039 20.5259 10.6749 21.6004 12.0004 21.6004C13.3259 21.6004 14.4004 20.5259 14.4004 19.2004C14.4004 17.8749 13.3259 16.8004 12.0004 16.8004C10.6749 16.8004 9.60039 17.8749 9.60039 19.2004Z"
                                        fill="#6B7280"
                                    />
                                </svg>
                            </Dropdown>
                        )}
                    </div>
                );
            },
        },
    ];

    const columns = clientId ? allColumns.filter((col) => col.key !== 'providerFirstName') : allColumns;

    return (
        <div className={s.wrap}>
            <CommonTable
                className={`commonTable ${s.paymentTable}`}
                columns={columns}
                data={dataList || []}
                loading={loading}
                bordered
                rowKey="appointmentId"
                onChange={handleTableChange}
                pagination={{
                    pageSize: 10,
                    current: page,
                    showSizeChanger: false,
                    total: listTotal,
                    showTotal: (total, range) => `${total} records`,
                }}
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: 'No earnings yet',
                }}
            />
            <Modal
                width="90%"
                open={showPaylaterModal}
                className={`${commonS.modalFixHeightWrap} ${s.modalWrap}`}
                title="Grow your practice faster with Klarity Pay Later (Powered by Kanmon)"
                onCancel={() => {
                    setShowPaylaterModal(false);
                }}
                footer={null}
                destroyOnClose
            >
                <div className={s.modalWrap}>
                    <FinancialsLandingPage
                        onClickedApplyNow={() => {
                            refetchKanmonInfo();
                            setAutoShowKanmonModal(true);
                        }}
                        doShowKanmonModal={() => {
                            setShowPaylaterModal(false);
                            if (kanmonConnect) {
                                kanmonConnect.show();
                            } else {
                                refetchKanmonInfo();
                            }
                        }}
                        isModal
                    />
                </div>
            </Modal>
        </div>
    );
};

export default BillingInvoiceTable;
